import isPlainObject from 'lodash.isplainobject';

import {
  type IAdditionalFieldConfig,
  type IAttachedDocumentResponse,
  type IItemResponse,
  type ILpcoResponse,
  type IPackage,
  type ITreatment,
} from '../types/response';
import { type IAdditionalField, type IDBOptionAdditionalField } from '../types/request';
import { DataType, QuotaType, TypeOfUse } from '../types/enums';

import {
  isValidArrayField,
  isValidBoolean,
  isValidDate,
  isValidEmail,
  isValidEnum,
  isValidNumber,
  isValidPhoneNumber,
  isValidString,
} from './importValidationUtils';

type TLicenseImportData = Omit<
  ILpcoResponse,
  | 'fees'
  | ' amountToBePaid'
  | ' approvalDate'
  | 'approvalReference'
  | 'beneficiaries'
  | 'billNumber'
  | 'id'
  | 'lastOperationDate'
  | 'licenseTypeId'
  | 'ogaStatus'
  | 'ogaStatusInNationalLang'
  | 'requestDate'
  | 'requestNumber'
  | 'status'
  | 'totalAmount'
  | 'transactionRecords'
  | 'userReferenceNumber'
  | 'validFrom'
  | 'validTo'
  | 'version'
  | 'approvalDate'
  | 'departmentCode'
  | 'amountToBePaid'
  | 'flow'
  | 'licenseTypeNature'
  | 'parentLpcoId'
>;

type TAttachedDocumentImportData = Omit<IAttachedDocumentResponse, 'id' | 'fileUrl' | 'fileName'>;

type TPackageImportData = Omit<IPackage, 'id'>;

type TTreatmentImportData = Omit<ITreatment, 'id'>;

type TItemImportData = Omit<
  IItemResponse,
  'id' | 'approvedAmount' | 'approvedUomCode' | 'approvedUomName' | 'remainingAmount' | 'parentItemId'
>;

const licenseValidations: Record<keyof TLicenseImportData, (key: string, value: unknown) => true> = {
  companyAddress: isValidString,
  companyCode: isValidString,
  companyDescription: isValidString,
  companyEmail: isValidEmail,
  companyPhone: isValidPhoneNumber,
  countryOfDestinationCode: isValidString,
  countryOfDestinationName: isValidString,
  customsOfficeCode: isValidString,
  customsOfficeName: isValidString,
  declarantCode: isValidString,
  declarantName: isValidString,
  departmentOfficeCode: isValidString,
  departmentOfficeName: isValidString,
  exporterNameAndAddress: isValidString,
  invoiceAmountInForeignCurrency: isValidNumber,
  invoiceAmountInNationalCurrency: isValidNumber,
  invoiceCurrencyCode: isValidString,
  invoiceCurrencyName: isValidString,
  invoiceExchangeRate: isValidNumber,
  licenseType: isValidNumber,
  ministryCode: isValidString,
  placeOfLoadingCode: isValidString,
  placeOfLoadingName: isValidString,
  placeOfUnloadingCode: isValidString,
  placeOfUnloadingName: isValidString,
  termsOfDeliveryCode: isValidString,
  termsOfDeliveryDescription: isValidString,
  typeOfUse: (key, value) => isValidEnum(key, value, TypeOfUse),
  transitCountryCode: isValidString,
  transitCountryName: isValidString,
  firstPointOfArrival: isValidString,
  meanOfTransportCode: isValidString,
  meanOfTransportName: isValidString,
  transportMeansOfIdentity: isValidString,
  voyageNumber: isValidString,
  items: (key, value) => isValidArrayField(key, value, isValidItem),
  attachedDocuments: (key, value) => isValidArrayField(key, value, isValidAttachedDocument),
  additionalFields: (key, value) => isValidArrayField(key, value, isValidAdditionalField),
  dbOptionAdditionalFields: (key, value) => isValidArrayField(key, value, isValidDbOptionAdditionalField),
};

const attachedDocumentValidations: Record<keyof TAttachedDocumentImportData, (key: string, value: unknown) => true> = {
  code: isValidString,
  name: isValidString,
  referenceNumber: isValidString,
  attachmentDate: isValidDate,
};

const additionalFieldValidations: Record<keyof IAdditionalField, (key: string, value: unknown) => true> = {
  fieldId: (key, value) => isValidNumber(key, value, true),
  fieldValue: () => true,
};

const dbOptionAdditionalFieldValidations: Record<
  keyof IDBOptionAdditionalField,
  (key: string, value: unknown) => true
> = {
  fieldId: (key, value) => isValidNumber(key, value, true),
  optionId: isValidNumber,
  fieldCode: isValidString,
  fieldValue1: isValidString,
  fieldValue2: isValidString,
};

const packageValidations: Record<keyof TPackageImportData, (key: string, value: unknown) => true> = {
  packageCode: (key, value) => isValidString(key, value, true),
  packageName: (key, value) => isValidString(key, value, true),
  packageQuantity: (key, value) => isValidNumber(key, value, true),
  numberOfNaturePackages: isValidString,
};

const treatmentValidations: Record<keyof TTreatmentImportData, (key: string, value: unknown) => true> = {
  validFrom: (key, value) => isValidDate(key, value, true),
  validTo: (key, value) => isValidDate(key, value, true),
  typeCode: (key, value) => isValidString(key, value, true),
  typeName: (key, value) => isValidString(key, value, true),
  accuracyCode: isValidString,
  accuracyName: isValidString,
  chemicalCode: (key, value) => isValidString(key, value, true),
  chemicalName: (key, value) => isValidString(key, value, true),
  duration: (key, value) => isValidString(key, value, true),
  concentration: (key, value) => isValidString(key, value, true),
  temperature: (key, value) => isValidString(key, value, true),
  additionalInformation: isValidString,
};

const itemValidations: Record<keyof TItemImportData, (key: string, value: unknown) => true> = {
  commercialDescription: isValidString,
  commodityCode: isValidNumber,
  commodityDescription: isValidString,
  countryOfOriginCode: isValidString,
  countryOfOriginName: isValidString,
  grossMass: isValidNumber,
  itemInvoiceAmountInForeignCurrency: isValidNumber,
  itemInvoiceAmountInNationalCurrency: isValidNumber,
  manufacturerName: isValidString,
  netMass: isValidNumber,
  packageMark: isValidString,
  packageTypeCode: isValidString,
  packageTypeName: isValidString,
  packageNumber: isValidNumber,
  quotaType: (key, value) => isValidEnum(key, value, QuotaType),
  requestedAmount: isValidNumber,
  requestedUomCode: isValidString,
  requestedUomName: isValidString,
  additionalFields: (key, value) => isValidArrayField(key, value, isValidAdditionalField),
  dbOptionAdditionalFields: (key, value) => isValidArrayField(key, value, isValidDbOptionAdditionalField),
  packages: (key, value) => isValidArrayField(key, value, isValidPackage),
  treatments: (key, value) => isValidArrayField(key, value, isValidTreatment),
};

function isValidAttachedDocument(value: unknown): true {
  if (isPlainObject(value)) {
    return Object.entries(value as Record<string, unknown>).every(
      ([key, value]) =>
        typeof attachedDocumentValidations[key as keyof typeof attachedDocumentValidations] !== 'function' ||
        attachedDocumentValidations[key as keyof typeof attachedDocumentValidations](key, value)
    ) as true;
  }

  throw new Error('Invalid Attached Document');
}

function isValidItem(value: unknown): true {
  if (isPlainObject(value)) {
    return Object.entries(value as Record<string, unknown>).every(
      ([key, value]) =>
        typeof itemValidations[key as keyof typeof itemValidations] !== 'function' ||
        itemValidations[key as keyof typeof itemValidations](key, value)
    ) as true;
  }

  throw new Error('Invalid Item');
}

function isValidAdditionalField(value: unknown): true {
  if (isPlainObject(value)) {
    return Object.entries(additionalFieldValidations).every(([key, method]) => {
      if (!Object.hasOwn(value as Record<string, unknown>, key)) {
        throw new Error('Invalid Additional Field');
      }

      return method(key, (value as Record<string, unknown>)[key]);
    }) as true;
  }

  throw new Error('Invalid Additional Field');
}

function isValidDbOptionAdditionalField(value: unknown): true {
  if (isPlainObject(value)) {
    return Object.entries(dbOptionAdditionalFieldValidations).every(([key, method]) => {
      if (!Object.hasOwn(value as Record<string, unknown>, key)) {
        throw new Error('Invalid DbOption Additional Field');
      }

      return method(key, (value as Record<string, unknown>)[key]);
    }) as true;
  }

  throw new Error('Invalid DbOptionAdditional Field');
}

function isValidPackage(value: unknown): true {
  if (isPlainObject(value)) {
    return Object.entries(packageValidations).every(([key, method]) => {
      if (!Object.hasOwn(value as Record<string, unknown>, key)) {
        throw new Error('Invalid Package');
      }

      return method(key, (value as Record<string, unknown>)[key]);
    }) as true;
  }

  throw new Error('Invalid Package');
}

function isValidTreatment(value: unknown): true {
  if (isPlainObject(value)) {
    return Object.entries(treatmentValidations).every(([key, method]) => {
      if (!Object.hasOwn(value as Record<string, unknown>, key)) {
        throw new Error('Invalid Treatment');
      }

      return method(key, (value as Record<string, unknown>)[key]);
    }) as true;
  }

  throw new Error('Invalid Treatment');
}

export function checkIsValidAdditionalFieldValue(value: unknown, config?: IAdditionalFieldConfig): true {
  if (!config) {
    return true;
  }

  if (config.dataType === DataType.DECIMAL || config.dataType === DataType.INTEGER) {
    return isValidNumber(config.englishLabel, value);
  }

  if (config.dataType === DataType.TEXT || config.dataType === DataType.TEXTAREA) {
    return isValidString(config.englishLabel, value);
  }

  if (config.dataType === DataType.CHECKBOX) {
    return isValidBoolean(config.englishLabel, value);
  }

  if (config.dataType === DataType.DATE) {
    return isValidDate(config.englishLabel, value);
  }

  return true;
}

function checkIsValidLicenseTypeData(value: unknown): true {
  if (isPlainObject(value)) {
    return Object.entries(value as Record<string, unknown>).every(
      ([key, value]) =>
        typeof licenseValidations[key as keyof typeof licenseValidations] !== 'function' ||
        licenseValidations[key as keyof typeof licenseValidations](key, value)
    ) as true;
  }

  throw new Error('Invalid License Data');
}

export default checkIsValidLicenseTypeData;
